/**
 * Client-side ability TARGET TYPE per ability — AUTO-DERIVED from content
 * (#352), mirroring `abilityFacts.ts`'s build-time `import.meta.glob` + narrow
 * schema pattern: an ability's target type ("enemy"/"ally"/"self"/"ground"/
 * "none") is exactly as visible to a player as its NAME — it's the first
 * thing anyone learns about an ability from using or being hit by it — so,
 * like `abilityLabels.ts`'s name auto-derive (#274), there's no balance
 * reason to hand-mirror it into a drift-prone table; read it straight off
 * content instead.
 *
 * Single consumer at MVP: `ActionBar.tsx`'s #352 range-based keybind color —
 * an "enemy"/"ally" ability checks distance to the caster's current
 * hostile/friendly target; a "ground" (skillshot) ability checks distance to
 * the last known cursor world position; "self"/"none" abilities have no
 * range-relevant target to check at all (always the neutral color).
 */

import { z } from "zod";
import {
  ABILITY_ID_SCHEMA,
  TARGET_TYPE_SCHEMA,
  type AbilityId,
  type ChampionId,
  type TargetType,
} from "@pit/protocol";
import { abilityPoolFor } from "./abilityPool.js";

/** Narrow schema: only `id` + `targetType` survive the parse. */
const ABILITY_TARGET_TYPE_SCHEMA = z.object({
  id: ABILITY_ID_SCHEMA,
  targetType: TARGET_TYPE_SCHEMA,
});

/**
 * Build-time map of every on-disk ability JSON → its RAW text, parsed down to
 * `{ id, targetType }`. Same literal-`import.meta.glob` rule as
 * `abilityCatalog.ts`/`abilityFacts.ts`: the call MUST be the literal form
 * for Vite's static transform.
 */
function loadContentTargetTypes(): ReadonlyMap<AbilityId, TargetType> {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/data/abilities/*.json",
    { eager: true, query: "?raw", import: "default" },
  );
  const out = new Map<AbilityId, TargetType>();
  for (const path of Object.keys(modules).sort()) {
    const raw = modules[path];
    if (raw === undefined) continue;
    const parsed = ABILITY_TARGET_TYPE_SCHEMA.parse(JSON.parse(raw) as unknown);
    out.set(parsed.id, parsed.targetType);
  }
  return out;
}

/** Every content ability's target type, keyed by its opaque id. */
export const ABILITY_TARGET_TYPE_BY_ID: ReadonlyMap<AbilityId, TargetType> =
  loadContentTargetTypes();

/** Target type for the ability at `id`, or `null` if unknown. Pure lookup. */
export function abilityTargetTypeForId(id: AbilityId): TargetType | null {
  return ABILITY_TARGET_TYPE_BY_ID.get(id) ?? null;
}

/**
 * Target type for `slot` on `championId`, resolved via `abilityPool.ts`'s
 * content-canonical slot order (the same champion+slot → ability id
 * resolution `abilityFacts.ts` uses) — `null` if the slot is empty or the
 * ability has no derived target type (e.g. a synthetic/test-only id).
 */
export function abilityTargetType(championId: ChampionId, slot: number): TargetType | null {
  const pool = abilityPoolFor(championId);
  const id = pool[slot];
  if (id === undefined) return null;
  return abilityTargetTypeForId(id);
}
